import { type ReactElement } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { Autocomplete, TextField, createFilterOptions } from '@mui/material'
import { FormControl } from '@ui/forms'
import { DESCRIPTION_PRESETS } from '../../constants'

interface DescriptionOption {
  /** What the list shows -- the preset itself, or the "add" prompt. */
  label: string
  /** What the description becomes when the option is picked. */
  value: string
}

const presetOptions: DescriptionOption[] = DESCRIPTION_PRESETS.map(({ description }) => ({
  label: description,
  value: description,
}))

const filterPresets = createFilterOptions<DescriptionOption>({
  stringify: option => option.label,
})

/**
 * The description is one of a handful of recurring project kinds, so it is
 * picked rather than typed -- but anything can be written instead, offered as
 * an "add" entry once it matches no preset. Either way the description stays
 * a plain string, so nothing downstream knows about the presets.
 */
const DescriptionField = (): ReactElement => {
  const { control } = useFormContext()
  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({ name: 'description', control })

  const description: string = value ?? ''

  return (
    <FormControl label="Beschreibung" error={error?.message}>
      <Autocomplete<DescriptionOption, false, false, true>
        freeSolo
        handleHomeEndKeys
        options={presetOptions}
        value={description ? { label: description, value: description } : null}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        getOptionLabel={option => (typeof option === 'string' ? option : option.value)}
        filterOptions={(options, params) => {
          const filtered = filterPresets(options, params)
          const typed = params.inputValue.trim()

          if (typed && !options.some(option => option.value === typed)) {
            filtered.push({ label: `"${typed}" nutzen`, value: typed })
          }

          return filtered
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.label}>
            {option.label}
          </li>
        )}
        onChange={(event, newValue) =>
          onChange(typeof newValue === 'string' ? newValue : newValue?.value ?? '')
        }
        // Typing is a description in its own right, so it counts even when the
        // "add" entry is never clicked.
        onInputChange={(event, inputValue, reason) => {
          if (reason === 'input') onChange(inputValue)
        }}
        onBlur={onBlur}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            placeholder="Auswählen oder eingeben"
            data-cy="input-description"
          />
        )}
      />
    </FormControl>
  )
}

export default DescriptionField
